'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Delete02Icon, Download01Icon } from '@terra/ui-icons';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';
import { MetaDefault } from '../../../components/typography';
import { formatFileSize } from '../utils';
import type { DocumentationDisplayFile } from './applicationDocumentation.types';

const APPLICATION_DOCUMENTATION_FILE_LIST_MAX_HEIGHT = '8.5rem';

export type ApplicationDocumentationFileListProps = {
  files: DocumentationDisplayFile[];
  onRemoveFile: ({ fileIndex }: { fileIndex: number }) => void;
  onDownloadFile?: ({ fileIndex }: { fileIndex: number }) => void;
  deleteAriaLabel: string;
  omitFileRowId?: string | null;
  scrollable?: boolean;
  isReadOnly?: boolean;
};

const toFileRowKey = ({
  file,
  fileIndex,
}: {
  file: DocumentationDisplayFile;
  fileIndex: number;
}) =>
  file.id ?? `${file.name}-${file.size}-${file.lastModified ?? 0}-${fileIndex}`;

export const ApplicationDocumentationFileList = ({
  files,
  onRemoveFile,
  onDownloadFile,
  deleteAriaLabel,
  omitFileRowId = null,
  scrollable,
  isReadOnly,
}: ApplicationDocumentationFileListProps) => {
  const tCommon = useTranslations('designSystem.common');
  const downloadLabel = tCommon('download');

  if (files.length === 0) {
    return null;
  }

  return (
    <Box
      w="full"
      {...(scrollable
        ? {
            minH: 0,
            maxH: APPLICATION_DOCUMENTATION_FILE_LIST_MAX_HEIGHT,
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const,
          }
        : {})}
    >
      <VStack alignItems="stretch" gap={3} w="full">
        {files.map((file, fileIndex) =>
          omitFileRowId && file.id === omitFileRowId ? null : (
            <HStack
              key={toFileRowKey({ file, fileIndex })}
              w="full"
              justifyContent="space-between"
              alignItems="center"
              gap={3}
              bg="bg.neutral.light"
              borderWidth="1px"
              borderColor="border.neutral.subtle"
              borderRadius="md"
              px={3}
              py={3}
            >
              <VStack alignItems="flex-start" gap={0.5} flex={1} minW={0}>
                <MetaDefault color="fg.neutral.default" truncate w="full">
                  {file.name}
                </MetaDefault>
                <MetaDefault color="fg.neutral.muted">
                  {formatFileSize(file.size)}
                </MetaDefault>
              </VStack>
              <HStack gap={1} flexShrink={0}>
                {file.id && onDownloadFile ? (
                  <Tooltip title={downloadLabel}>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={downloadLabel}
                      onClick={() => onDownloadFile({ fileIndex })}
                    >
                      <Download01Icon size={16} />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {!isReadOnly ? (
                  <Tooltip title={deleteAriaLabel}>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={deleteAriaLabel}
                      onClick={() => onRemoveFile({ fileIndex })}
                    >
                      <Delete02Icon size={16} />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </HStack>
            </HStack>
          ),
        )}
      </VStack>
    </Box>
  );
};
